import { gql, useQuery } from '@apollo/client';
import {
  Loyalty_GetEnrollmentDetailsQuery,
  Loyalty_GetEnrollmentDetailsQueryVariables,
} from '@/types';
import { LoyaltyEnrollmentFormConfig } from '@/components/loyalty/loyalty-enrollment-form-types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMemo } from 'react';

export enum LoyaltyProgramRestaurantStatus {
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
  Pending = 'PENDING',
}

export enum LoyaltyProgramRestaurantUserRole {
  LoyaltyAdmin = 'LOYALTY_ADMIN',
}

export interface LoyaltyTermsState {
  enrollmentFormConfig: LoyaltyEnrollmentFormConfig | null;
  enrollmentUUID: string | null;
  isLoading: boolean;
  latestAcceptanceDisclaimer: string | null;
  latestTermsSummary: string | null;
  latestTermsUrl: string | null;
  latestTermsUUID: string | null;
  programBannerImageUrl: string | null;
  programBannerText: string | null;
  programEnrollmentCompletionText: string | null;
  refetch: () => void;
  shouldPromptForTerms: boolean;
}

// `supplier_loyalty_programs` is intentionally not a root query field for
// the restaurant-app role (its Hasura YAML sets `query_root_fields: []` —
// access is only allowed through relationships). We route through
// `businesses`, which IS root-queryable, and traverse the array
// relationship from there.
export const LOYALTY_GET_ENROLLMENT_DETAILS_QUERY = gql`
  query Loyalty_GetEnrollmentDetails(
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
    $userUUID: uuid!
  ) {
    businesses(where: { uuid: { _eq: $supplierUUID } }) {
      supplier_loyalty_programs {
        loyalty_program {
          uuid
          banner_image_url
          banner_text
          enrollment_completion_text
          enrollment_form_config
          loyalty_program_terms(order_by: { created_at: desc }, limit: 1) {
            acceptance_disclaimer
            summary
            terms_url
            uuid
            version
          }
        }
        permissions
        supplier_loyalty_program_restaurants(
          where: { restaurant_uuid: { _eq: $restaurantUUID } }
          limit: 1
        ) {
          loyalty_terms_acceptance_logs(
            order_by: { created_at: desc }
            limit: 1
          ) {
            loyalty_program_terms_uuid
            uuid
          }
          enrollment_form_responses
          restaurant_uuid
          status
          supplier_loyalty_program_restaurant_users(
            where: { employee_uuid: { _eq: $userUUID } }
          ) {
            role
            uuid
          }
          uuid
        }
        uuid
      }
      uuid
    }
  }
`;

export const useLoyaltyTerms = (
  supplierUUID: string,
  programUUID?: string,
  skip = false,
): LoyaltyTermsState => {
  const { selectedRestaurantUUID, user } = useAuthenticatedUserContext();
  const { data, loading, refetch } = useQuery<
    Loyalty_GetEnrollmentDetailsQuery,
    Loyalty_GetEnrollmentDetailsQueryVariables
  >(LOYALTY_GET_ENROLLMENT_DETAILS_QUERY, {
    skip,
    variables: {
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID,
      userUUID: user.uuid,
    },
  });

  return useMemo(() => {
    const inactive: LoyaltyTermsState = {
      enrollmentFormConfig: null,
      enrollmentUUID: null,
      isLoading: loading,
      latestAcceptanceDisclaimer: null,
      latestTermsSummary: null,
      latestTermsUrl: null,
      latestTermsUUID: null,
      programBannerImageUrl: null,
      programBannerText: null,
      programEnrollmentCompletionText: null,
      refetch,
      shouldPromptForTerms: false,
    };

    const programs = data?.businesses?.[0]?.supplier_loyalty_programs ?? [];
    const program = programUUID
      ? programs.find((entry) => entry.loyalty_program.uuid === programUUID)
      : programs[0];
    if (!program || program.permissions.length === 0) {
      return inactive;
    }

    const enrollment = program.supplier_loyalty_program_restaurants[0];
    const latestTerms =
      program.loyalty_program.loyalty_program_terms[0] ?? null;
    const latestAcceptance = enrollment?.loyalty_terms_acceptance_logs[0];

    const needsTermsAcceptance =
      Boolean(latestTerms) &&
      latestTerms.uuid !== latestAcceptance?.loyalty_program_terms_uuid;
    const canSignTerms =
      (enrollment?.supplier_loyalty_program_restaurant_users.length ?? 0) > 0;
    const isEnrolled =
      enrollment?.status === LoyaltyProgramRestaurantStatus.Pending;

    const enrollmentFormConfig = program.loyalty_program
      .enrollment_form_config as LoyaltyEnrollmentFormConfig | null;
    const hasAnswered = Boolean(enrollment?.enrollment_form_responses?.length);
    return {
      enrollmentFormConfig:
        hasAnswered || !enrollmentFormConfig?.questions?.length
          ? null
          : enrollmentFormConfig,
      enrollmentUUID: enrollment?.uuid ?? null,
      isLoading: loading,
      latestAcceptanceDisclaimer: latestTerms?.acceptance_disclaimer ?? null,
      latestTermsSummary: latestTerms?.summary ?? null,
      latestTermsUrl: latestTerms?.terms_url ?? null,
      latestTermsUUID: latestTerms?.uuid ?? null,
      programBannerImageUrl: program.loyalty_program.banner_image_url ?? null,
      programBannerText: program.loyalty_program.banner_text ?? null,
      programEnrollmentCompletionText:
        program.loyalty_program.enrollment_completion_text ?? null,
      refetch,
      shouldPromptForTerms: isEnrolled && needsTermsAcceptance && canSignTerms,
    };
  }, [data, loading, programUUID, refetch]);
};
